<template>
    <div class="main-container purger-legal-page">
        <section class="purger-legal-hero section-space--pt_90 section-space--pb_40">
            <div class="container">
                <div class="purger-legal-header">
                    <router-link class="purger-back" to="/apps/purger"><i class="fa fa-arrow-left"></i> Back</router-link>
                    <h1>Privacy Policy - Purger for Plex</h1>
                </div>
            </div>
        </section>

        <section class="section-space--pb_120">
            <div class="container">
                <div class="purger-legal-content purger-legal-content--wide">
                    <h1>Privacy Policy</h1>
                    <p class="last-updated">Last updated: January 13, 2026</p>

                    <div class="highlight">
                        <strong>Privacy First:</strong> We believe in protecting your privacy. All analytics and crash reports are completely anonymous. We do NOT store your Plex credentials or authentication tokens on our servers.
                    </div>

                    <p>This Privacy Policy describes how Purger for Plex ("App", "we", "our", or "us") collects, uses, and shares information when you use our iOS application.</p>

                    <h2>1. Information We Collect</h2>

                    <h3>1.1 Information You Provide</h3>
                    <p>When you use the App, you may provide:</p>
                    <ul>
                        <li><strong>Plex Account Information</strong> - You authenticate using Plex's OAuth2 system. <strong>We do NOT store your Plex username, password, or authentication tokens on our servers.</strong> These are stored securely and locally on your device only.</li>
                    </ul>

                    <h3>1.2 Information Automatically Collected</h3>
                    <p><strong>All automatically collected data is anonymous and cannot be used to identify you personally.</strong></p>

                    <table>
                        <thead>
                            <tr>
                                <th>Type of Data</th>
                                <th>What We Collect</th>
                                <th>Purpose</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Anonymous Analytics</strong></td>
                                <td>App usage patterns, feature interactions, screen views</td>
                                <td>Improve app functionality and user experience</td>
                            </tr>
                            <tr>
                                <td><strong>Crash Reports</strong></td>
                                <td>Anonymous error logs, device model, iOS version, app version</td>
                                <td>Identify and fix bugs and crashes</td>
                            </tr>
                            <tr>
                                <td><strong>Anonymous User ID</strong></td>
                                <td>Firebase Anonymous Authentication ID</td>
                                <td>Manage app functionality and subscriptions</td>
                            </tr>
                            <tr>
                                <td><strong>Purchase Information</strong></td>
                                <td>Subscription status, purchase history</td>
                                <td>Manage Pro features and billing (handled by Apple)</td>
                            </tr>
                        </tbody>
                    </table>

                    <h3>1.3 Information NOT Collected</h3>
                    <p>We explicitly <strong>DO NOT</strong> collect:</p>
                    <ul>
                        <li>[x] Your name, email address, or personal contact information</li>
                        <li>[x] Your Plex credentials (username/password)</li>
                        <li>[x] Your Plex authentication tokens (stored locally on your device only)</li>
                        <li>[x] Your media file names, titles, or content</li>
                        <li>[x] Information about what media you view, delete, or keep</li>
                        <li>[x] Your Plex server IP addresses or connection details</li>
                        <li>[x] Your location data</li>
                        <li>[x] Your contacts or photos</li>
                    </ul>

                    <h2>2. How We Use Your Information</h2>

                    <p>We use the anonymous information we collect to:</p>
                    <ul>
                        <li><strong>Provide and maintain the App</strong> - Enable core functionality and features</li>
                        <li><strong>Improve the App</strong> - Analyze usage patterns to enhance user experience</li>
                        <li><strong>Fix bugs and crashes</strong> - Identify and resolve technical issues</li>
                        <li><strong>Manage subscriptions</strong> - Handle Pro feature access through RevenueCat</li>
                        <li><strong>Provide customer support</strong> - Respond to your questions and concerns</li>
                    </ul>

                    <p><strong>We do NOT:</strong></p>
                    <ul>
                        <li>[x] Sell your information to third parties</li>
                        <li>[x] Use your information for advertising or marketing</li>
                        <li>[x] Share your information with data brokers</li>
                        <li>[x] Track you across other apps or websites</li>
                    </ul>

                    <h2>3. Third-Party Services</h2>

                    <p>The App uses the following third-party services to function. Each service has its own privacy policy:</p>

                    <h3>3.1 Plex</h3>
                    <ul>
                        <li><strong>Purpose:</strong> Authentication and media server access</li>
                        <li><strong>Data Shared:</strong> OAuth2 authentication (handled entirely between your device and Plex servers)</li>
                        <li><strong>Privacy Policy:</strong> <a href="https://www.plex.tv/about/privacy-legal/" target="_blank" rel="noopener">https://www.plex.tv/about/privacy-legal/</a></li>
                    </ul>

                    <h3>3.2 Firebase (Google)</h3>
                    <ul>
                        <li><strong>Purpose:</strong> Anonymous analytics, crash reporting, remote configuration, and anonymous authentication</li>
                        <li><strong>Data Shared:</strong> Anonymous usage data, crash logs, device information</li>
                        <li><strong>Privacy Policy:</strong> <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener">https://firebase.google.com/support/privacy</a></li>
                    </ul>

                    <h3>3.3 RevenueCat</h3>
                    <ul>
                        <li><strong>Purpose:</strong> Subscription and payment management</li>
                        <li><strong>Data Shared:</strong> Anonymous user ID, purchase history (managed through Apple)</li>
                        <li><strong>Privacy Policy:</strong> <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener">https://www.revenuecat.com/privacy</a></li>
                    </ul>

                    <h3>3.4 Sentry</h3>
                    <ul>
                        <li><strong>Purpose:</strong> Anonymous error tracking and crash reporting</li>
                        <li><strong>Data Shared:</strong> Anonymous error logs, device model, iOS version</li>
                        <li><strong>Privacy Policy:</strong> <a href="https://sentry.io/privacy/" target="_blank" rel="noopener">https://sentry.io/privacy/</a></li>
                    </ul>

                    <h3>3.5 Apple (App Store and In-App Purchases)</h3>
                    <ul>
                        <li><strong>Purpose:</strong> Payment processing and subscription management</li>
                        <li><strong>Data Shared:</strong> Apple ID, payment information (managed entirely by Apple)</li>
                        <li><strong>Privacy Policy:</strong> <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener">https://www.apple.com/legal/privacy/</a></li>
                    </ul>

                    <h2>4. Data Storage and Security</h2>

                    <h3>4.1 Local Storage</h3>
                    <p>The following data is stored <strong>locally on your device only</strong> using iOS secure storage (UserDefaults and Keychain):</p>
                    <ul>
                        <li>Plex authentication tokens</li>
                        <li>Your selected Plex server and library preferences</li>
                        <li>App settings and preferences</li>
                        <li>Deletion history (Pro users only)</li>
                    </ul>
                    <p><strong>This data is NOT synced to our servers or iCloud.</strong> If you delete the app, this data is permanently removed from your device.</p>

                    <h3>4.2 Cloud Storage</h3>
                    <p>Anonymous analytics and crash reports are stored on Firebase and Sentry servers. These services use industry-standard security measures to protect data.</p>

                    <h3>4.3 No Personal Data on Our Servers</h3>
                    <p>We do not operate servers that store your personal information, Plex credentials, or media content. All sensitive data remains on your device.</p>

                    <h2>5. Data Retention</h2>

                    <ul>
                        <li><strong>Local Data:</strong> Remains on your device until you delete the app or log out</li>
                        <li><strong>Analytics Data:</strong> Automatically deleted after 2 months (Firebase default)</li>
                        <li><strong>Crash Reports:</strong> Automatically deleted after 90 days (Sentry default)</li>
                        <li><strong>Purchase History:</strong> Retained by Apple and RevenueCat according to their policies</li>
                    </ul>

                    <h2>6. Your Rights and Choices</h2>

                    <h3>6.1 Access and Deletion</h3>
                    <p>Since we collect only anonymous data, there is no personal profile associated with you. However, you can:</p>
                    <ul>
                        <li><strong>Delete all local data:</strong> Delete the app from your device</li>
                        <li><strong>Stop analytics collection:</strong> Unfortunately, iOS does not allow opting out of Firebase Analytics at the app level. Deleting the app stops all data collection.</li>
                        <li><strong>Request data deletion:</strong> Contact us at dualbit@dualb.it to request deletion of any data associated with your anonymous user ID</li>
                    </ul>

                    <h3>6.2 Do Not Track</h3>
                    <p>The App does not track you across other apps or websites. We do not respond to "Do Not Track" signals because we do not engage in tracking behavior.</p>

                    <h2>7. Children's Privacy</h2>
                    <p>The App is not directed at children under 13 years of age. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided personal information to us, please contact us at dualbit@dualb.it.</p>

                    <h2>8. International Data Transfers</h2>
                    <p>The App uses services that may store and process data in various countries, including the United States. By using the App, you consent to the transfer of your anonymous data to these locations.</p>

                    <h3>8.1 For Users in the European Economic Area (EEA)</h3>
                    <p>If you are located in the EEA, we rely on the following legal bases to process your data:</p>
                    <ul>
                        <li><strong>Legitimate Interest:</strong> To provide, improve, and secure the App</li>
                        <li><strong>Consent:</strong> For optional features or services (where applicable)</li>
                        <li><strong>Contractual Necessity:</strong> To fulfill our obligations in the Terms of Service</li>
                    </ul>
                    <p>You have the right to:</p>
                    <ul>
                        <li>Access your data</li>
                        <li>Request data deletion</li>
                        <li>Object to data processing</li>
                        <li>File a complaint with your local data protection authority</li>
                    </ul>

                    <h2>9. Changes to This Privacy Policy</h2>
                    <p>We may update this Privacy Policy from time to time. We will notify you of material changes by:</p>
                    <ul>
                        <li>Updating the "Last Updated" date at the top of this policy</li>
                        <li>Displaying an in-app notification</li>
                        <li>Sending an email (if you have provided one for support purposes)</li>
                    </ul>
                    <p>Continued use of the App after changes constitutes acceptance of the updated Privacy Policy.</p>

                    <h2>10. Contact Us</h2>
                    <div class="contact">
                        <p><strong>Developer:</strong> DualBit</p>
                        <p><strong>Email:</strong> <a href="mailto:dualbit@dualb.it">dualbit@dualb.it</a></p>
                        <p><strong>Privacy Questions:</strong> If you have questions or concerns about this Privacy Policy or our data practices, please contact us at the email address above.</p>
                    </div>

                    <h2>11. California Privacy Rights (CCPA)</h2>
                    <p>If you are a California resident, you have the right to:</p>
                    <ul>
                        <li>Know what personal information is collected</li>
                        <li>Know whether personal information is sold or disclosed</li>
                        <li>Opt-out of the sale of personal information</li>
                        <li>Request deletion of personal information</li>
                        <li>Non-discrimination for exercising your rights</li>
                    </ul>
                    <p><strong>We do not sell personal information.</strong> Since we collect only anonymous data, there is no personal profile to access or delete. Contact us at dualbit@dualb.it for any CCPA-related questions.</p>

                    <hr class="purger-legal-divider">

                    <div class="purger-legal-summary">
                        <h3>Summary: What You Need to Know</h3>
                        <ul>
                            <li>[ok] All analytics and crash reports are <strong>completely anonymous</strong></li>
                            <li>[ok] We <strong>do NOT store</strong> your Plex credentials or authentication tokens on our servers</li>
                            <li>[ok] We <strong>do NOT track</strong> what media you view, delete, or keep</li>
                            <li>[ok] We <strong>do NOT sell</strong> your data to anyone</li>
                            <li>[ok] We <strong>do NOT use</strong> your data for advertising</li>
                            <li>[ok] Your sensitive data stays <strong>on your device only</strong></li>
                        </ul>
                    </div>

                    <p class="purger-legal-footer">(c) 2026 DualBit. All rights reserved.<br>Purger for Plex is not affiliated with Plex Inc.</p>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
    export default {
        name: 'PurgerPrivacy',
        metaInfo: {
            title: 'Privacy Policy - Purger for Plex',
            htmlAttrs: {
                lang: 'en',
                amp: true
            }
        }
    }
</script>

<style lang="scss" scoped>
    .purger-legal-hero {
        padding-top: 160px;
    }

    .purger-legal-header {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .purger-back {
        color: #27397c;
        font-weight: 600;
    }

    .purger-legal-content {
        max-width: 960px;
    }

    .purger-legal-content--wide table {
        width: 100%;
    }

    .purger-legal-content h1 {
        font-size: 30px;
        margin-bottom: 10px;
    }

    .purger-legal-content h2 {
        font-size: 22px;
        margin-top: 28px;
    }

    .purger-legal-content h3 {
        font-size: 18px;
        margin-top: 22px;
    }

    .last-updated {
        color: #666;
    }

    .purger-legal-divider {
        margin: 40px 0;
        border: none;
        border-top: 1px solid #ddd;
    }

    .purger-legal-summary {
        background: #e8f4ff;
        padding: 20px;
        border-radius: 5px;
        margin: 30px 0;
    }

    .purger-legal-summary h3 {
        margin-top: 0;
        color: #007aff;
    }

    .purger-legal-summary {
        color: #000000;
    }

    .purger-legal-summary h3 {
        color: #007aff;
    }

    .purger-legal-summary li {
        color: #000000;
    }

    :deep(.dark) .purger-legal-summary {
        color: #000000 !important;
    }

    :deep(.dark) .purger-legal-summary h3 {
        color: #0b5ed7 !important;
    }

    :deep(.dark) .purger-legal-summary li {
        color: #000000 !important;
    }

    .purger-legal-footer {
        text-align: center;
        color: #666;
        font-size: 14px;
    }

    @media (max-width: 767px) {
        .purger-legal-hero {
            padding-top: 90px;
        }
    }
</style>
